// @ts-check
// Painel lateral do editor (spec 2.0.0, fluxos.md seção 20.7; contrato 09 de fluxos, seção 17): cabeçalho com título,
// texto curto de ajuda e fechar; corpo rolável; rodapé fixo quando há ação. Abaixo de 1.280 px fica por cima do canvas
// (com sombra); a partir de 1.280 px fica ao lado; no celular é uma folha em tela cheia com "Voltar ao fluxo" (ou o
// texto de quem usa o painel, como o "Voltar ao bot" dos bots).
import { html } from 'htm/preact';
import { useEffect, useRef } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { LightIconButton } from '../../ui/button.js';
import { useIsMobile } from '../../ui/hooks.js';
import { t } from '../../i18n/index.js';

/**
 * @param {{ title: string, help?: string | null, onClose: () => void, children?: any, footer?: any, testId?: string, panelKey?: string,
 *   headerExtra?: any, backLabel?: string | null }} props
 *   panelKey: muda quando o conteúdo é outro (passo, entrada, configurações): o foco vai para o título
 */
export function FlowSidePanel({ title, help = null, onClose, children, footer, testId, panelKey = '', headerExtra = null, backLabel = null }) {
  const isMobile = useIsMobile();
  const titleRef = useRef(/** @type {HTMLHeadingElement | null} */ (null));
  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
  }, [panelKey]);
  // ao fechar, o foco volta ao passo (ou à entrada) no canvas, em vez de cair no body
  const closeAndReturnFocus = () => {
    const step = /** @type {HTMLElement | null} */ (document.querySelector('[data-focus-id][data-selected="true"]'));
    onClose();
    setTimeout(() => {
      const target = step?.isConnected ? step : /** @type {HTMLElement | null} */ (document.querySelector('[data-focus-id][tabindex="0"]'));
      target?.focus({ preventScroll: true });
    }, 0);
  };
  /** Esc fecha o painel (fluxos.md 20.10); um seletor nativo aberto ou um menu interno tratam o próprio Esc. @param {KeyboardEvent} event */
  const handleKeyDown = (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;
    if (/** @type {HTMLElement} */ (event.target)?.tagName === 'SELECT') return;
    event.preventDefault();
    event.stopPropagation();
    closeAndReturnFocus();
  };
  return html`<aside class="flow-side-panel" aria-label=${title} data-testid=${testId} data-mobile=${isMobile ? 'true' : undefined} onKeyDown=${handleKeyDown}>
    <header class="flow-side-panel-header">
      ${isMobile
        ? html`<button type="button" class="flow-back-link flow-side-panel-back" onClick=${closeAndReturnFocus}><${Icon} name="IconChevronLeft" size=${16} /><span>${backLabel ?? t('flows.panel.backToFlow')}</span></button>`
        : null}
      <div class="flow-side-panel-titles">
        <h2 class="flow-side-panel-title" tabindex="-1" ref=${titleRef}>${title}</h2>
        ${help ? html`<p class="flow-side-panel-help">${help}</p>` : null}
      </div>
      ${headerExtra}
      ${isMobile ? null : html`<${LightIconButton} icon="IconX" size="medium" ariaLabel=${t('flows.panel.close')} onClick=${closeAndReturnFocus} />`}
    </header>
    <div class="flow-side-panel-body">${children}</div>
    ${footer ? html`<footer class="flow-side-panel-footer">${footer}</footer>` : null}
  </aside>`;
}
